/**
 * Optimised Route Calculator for Paragliding Tasks
 *
 * Ported from @albatross/core (packages/core/src/task/optimize.ts).
 * Uses Web Mercator projection matching XCTrack's internal algorithm
 * with forward rubber-band passes and equal-angle bisection.
 *
 * The optimised route is the shortest path that enters each turnpoint
 * cylinder — the actual flyable route used for scoring, not the naive
 * center-to-center lines.
 */

// ── Types ────────────────────────────────────────────────────────

export interface Turnpoint {
  lat: number
  lon: number
  radius: number // metres
  type?: 'TAKEOFF' | 'SSS' | 'ESS'
}

export interface OptimizedPoint {
  lat: number
  lon: number
}

// ── FCC Distance (matching Albatross core) ───────────────────────

const DEG_TO_RAD = Math.PI / 180

function distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if (lat1 === lat2 && lon1 === lon2) return 0.0
  const dlat = lat2 - lat1
  const dlon = lon2 - lon1
  const fm = ((lat1 + lat2) / 2) * DEG_TO_RAD
  const k1 = 111.13209 - 0.56605 * Math.cos(2 * fm) + 0.0012 * Math.cos(4 * fm)
  const k2 = 111.41513 * Math.cos(fm) - 0.09455 * Math.cos(3 * fm) + 0.00012 * Math.cos(5 * fm)
  const dNS = k1 * dlat
  const dEW = k2 * dlon
  return Math.sqrt(dNS * dNS + dEW * dEW) * 1000
}

// ── Web Mercator projection (matching XCTrack) ───────────────────

const EARTH_CIRCUMFERENCE = 4.003017359204114e7

interface Pt {
  x: number
  y: number
}

function mercX(lon: number): number {
  const x = (lon + 180) / 360
  return x < 0 || x >= 1 ? x - Math.floor(x) : x
}

function mercY(lat: number): number {
  return (1 - Math.log(Math.tan((lat * Math.PI) / 360 + Math.PI / 4)) / Math.PI) / 2
}

function invMercX(x: number): number {
  return x * 360 - 180
}

function invMercY(y: number): number {
  return ((Math.atan(Math.exp(Math.PI - y * 2 * Math.PI)) * 2 - Math.PI / 2) * 180) / Math.PI
}

function radiusToMerc(radiusM: number, latDeg: number): number {
  return radiusM / (Math.cos(latDeg * DEG_TO_RAD) * EARTH_CIRCUMFERENCE)
}

// ── Optimization primitives ──────────────────────────────────────

function projectToward(cx: number, cy: number, r: number, tx: number, ty: number): Pt {
  const dx = tx - cx
  const dy = ty - cy
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len < 1e-20) return { x: cx + r, y: cy }
  return { x: cx + (dx / len) * r, y: cy + (dy / len) * r }
}

function equalAngleBisect(
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
  r: number,
): Pt {
  const daX = ax - cx
  const daY = ay - cy
  const dbX = bx - cx
  const dbY = by - cy
  const distA2 = daX * daX + daY * daY
  const distB2 = dbX * dbX + dbY * dbY

  if (distA2 < 1e-20) {
    const len = Math.sqrt(distB2)
    return { x: cx + (dbX / len) * r, y: cy + (dbY / len) * r }
  }
  if (distB2 < 1e-20) {
    const len = Math.sqrt(distA2)
    return { x: cx + (daX / len) * r, y: cy + (daY / len) * r }
  }

  const angA0 = Math.atan2(daY, daX)
  const angB0 = Math.atan2(dbY, dbX)

  let angA = angA0
  let angB = angB0
  let diff = angB - angA
  while (diff > Math.PI) diff -= 2 * Math.PI
  while (diff <= -Math.PI) diff += 2 * Math.PI
  if (diff > 0) {
    const tmp = angA
    angA = angB
    angB = tmp
  }
  if (angB > angA) angB -= 2 * Math.PI

  const hFunc = (angle: number): number => {
    const px = Math.cos(angle) * r
    const py = Math.sin(angle) * r
    const apx = px - daX
    const apy = py - daY
    const lenAP = Math.sqrt(apx * apx + apy * apy)
    const h1 = lenAP > 0 ? (apx * py - apy * px) / lenAP : 0
    const bpx = px - dbX
    const bpy = py - dbY
    const lenBP = Math.sqrt(bpx * bpx + bpy * bpy)
    const h2 = lenBP > 0 ? (bpx * py - bpy * px) / lenBP : 0
    return h1 + h2
  }

  const hA = hFunc(angA)
  const hB = hFunc(angB)

  if (hA * hB <= 0) {
    let lo = angB
    let hi = angA
    const hLo = hB
    for (let i = 0; i < 100 && hi - lo > 1e-10; i++) {
      const mid = (lo + hi) / 2
      if (hFunc(mid) * hLo > 0) {
        lo = mid
      } else {
        hi = mid
      }
    }
    const res = (lo + hi) / 2
    return { x: cx + Math.cos(res) * r, y: cy + Math.sin(res) * r }
  }

  const res = (angA + angB) / 2
  return { x: cx + Math.cos(res) * r, y: cy + Math.sin(res) * r }
}

function forwardPass(
  fx: number[], fy: number[],
  cx: number[], cy: number[],
  radii: number[],
  n: number,
): void {
  if (n < 2) return

  const p0 = projectToward(cx[0], cy[0], radii[0], fx[1], fy[1])
  fx[0] = p0.x
  fy[0] = p0.y

  for (let i = 1; i < n - 1; i++) {
    const ax = fx[i - 1]
    const ay = fy[i - 1]
    const bx = fx[i + 1]
    const by = fy[i + 1]
    const dx = bx - ax
    const dy = by - ay
    const seg2 = dx * dx + dy * dy
    const ecx = ax - cx[i]
    const ecy = ay - cy[i]
    const r = radii[i]

    if (seg2 < 1e-25) {
      const p = projectToward(cx[i], cy[i], r, ax, ay)
      fx[i] = p.x
      fy[i] = p.y
      continue
    }

    const bCoef = 2 * (ecx * dx + ecy * dy)
    const cCoef = ecx * ecx + ecy * ecy - r * r
    const disc = bCoef * bCoef - 4 * seg2 * cCoef

    if (disc < 0) {
      const p = equalAngleBisect(ax, ay, bx, by, cx[i], cy[i], r)
      fx[i] = p.x
      fy[i] = p.y
    } else {
      const sqrtD = Math.sqrt(disc)
      const aCoef = 2 * seg2
      const t1 = (-bCoef - sqrtD) / aCoef
      const t2 = (-bCoef + sqrtD) / aCoef
      if (t2 < 0 || t1 > 1) {
        const p = equalAngleBisect(ax, ay, bx, by, cx[i], cy[i], r)
        fx[i] = p.x
        fy[i] = p.y
      } else if (t1 >= 0 && t1 <= 1) {
        fx[i] = ax + dx * t1
        fy[i] = ay + dy * t1
      } else if (t2 >= 0 && t2 <= 1) {
        fx[i] = ax + dx * t2
        fy[i] = ay + dy * t2
      } else {
        const p = equalAngleBisect(ax, ay, bx, by, cx[i], cy[i], r)
        fx[i] = p.x
        fy[i] = p.y
      }
    }
  }

  const pN = projectToward(cx[n - 1], cy[n - 1], radii[n - 1], fx[n - 2], fy[n - 2])
  fx[n - 1] = pN.x
  fy[n - 1] = pN.y
}

function totalProjDist(fx: number[], fy: number[], n: number): number {
  let d = 0
  for (let i = 1; i < n; i++) {
    const dx = fx[i] - fx[i - 1]
    const dy = fy[i] - fy[i - 1]
    d += Math.sqrt(dx * dx + dy * dy)
  }
  return d
}

// ── Main optimizer ───────────────────────────────────────────────

const MAX_ITERATIONS = 500

/**
 * Compute the optimised route for a paragliding task.
 *
 * Uses Web Mercator projection for the 2D optimization (matching
 * XCTrack's algorithm), then converts back to lat/lon with geodesic
 * snap to ensure each point lies exactly on the cylinder radius.
 *
 * @param turnpoints - Ordered task turnpoints with lat, lon, radius, and optional type
 * @returns Array of optimized points (one per turnpoint) on cylinder edges
 */
export function computeOptimisedRoute(turnpoints: Turnpoint[]): OptimizedPoint[] {
  const n = turnpoints.length
  if (n === 0) return []
  if (n === 1) return [{ lat: turnpoints[0].lat, lon: turnpoints[0].lon }]

  // Determine SSS index from turnpoint types
  let sssIdx = -1
  for (let i = 0; i < n; i++) {
    if (turnpoints[i].type === 'SSS') sssIdx = i
  }
  // Goal is always the last turnpoint
  const goalIdx = n - 1
  const effectiveSss = sssIdx >= 0 ? sssIdx : 0
  if (effectiveSss >= goalIdx) {
    return turnpoints.map((tp) => ({ lat: tp.lat, lon: tp.lon }))
  }

  // Project all turnpoints to Mercator
  const allCx = turnpoints.map((tp) => mercX(tp.lon))
  const allCy = turnpoints.map((tp) => mercY(tp.lat))
  const allRadii = turnpoints.map((tp) => radiusToMerc(tp.radius, tp.lat))

  // Initialize fix positions shifted north (avoids degenerate cases)
  const allFx = [...allCx]
  const allFy = allCy.map((cy, i) => cy - allRadii[i])

  // WGS-84 radius correction
  for (let i = 0; i < n; i++) {
    if (allRadii[i] === 0) continue
    const projLat = invMercY(allFy[i])
    const projLon = invMercX(allFx[i])
    const geoDist = distance(turnpoints[i].lat, turnpoints[i].lon, projLat, projLon)
    if (geoDist > 1e-6) {
      allRadii[i] = (turnpoints[i].radius / geoDist) * allRadii[i]
    }
  }

  // Convergence threshold (~1cm)
  const threshold = 0.01 * radiusToMerc(1, turnpoints[0].lat)

  // Optimize entire route as one rubber-band
  let prev = totalProjDist(allFx, allFy, n)
  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    forwardPass(allFx, allFy, allCx, allCy, allRadii, n)
    const cur = totalProjDist(allFx, allFy, n)
    if (prev - cur <= threshold) break
    prev = cur
  }

  // Convert back to lat/lon
  const allPoints: OptimizedPoint[] = allFx.map((_, i) => {
    if (turnpoints[i].radius === 0) {
      return { lat: turnpoints[i].lat, lon: turnpoints[i].lon }
    }
    return {
      lat: invMercY(allFy[i]),
      lon: invMercX(allFx[i]),
    }
  })

  // Geodesic snap: ensure each point lies exactly on the cylinder radius
  for (let i = 0; i < n; i++) {
    if (turnpoints[i].radius === 0) continue
    const cLat = turnpoints[i].lat
    const cLon = turnpoints[i].lon
    const geoDist = distance(cLat, cLon, allPoints[i].lat, allPoints[i].lon)
    if (geoDist < 1e-6) continue
    const scale = turnpoints[i].radius / geoDist
    allPoints[i] = {
      lat: cLat + (allPoints[i].lat - cLat) * scale,
      lon: cLon + (allPoints[i].lon - cLon) * scale,
    }
  }

  return allPoints
}

/**
 * Convert optimised route to Leaflet-compatible positions array.
 */
export function optimisedRouteToPositions(turnpoints: Turnpoint[]): [number, number][] {
  return computeOptimisedRoute(turnpoints).map((p) => [p.lat, p.lon])
}
